import { useState, useRef, useCallback } from "react";

/**
 * Multi-row selection hook with Shift+click range support.
 *
 * Usage:
 *   const { selectedIds, isSelected, toggle, toggleAll, allSelected, clear } =
 *     useShiftSelection();
 *
 *   // In a row click/checkbox handler:
 *   <input
 *     type="checkbox"
 *     checked={isSelected(row.id)}
 *     onChange={(e) => toggle(row.id, idx, items, e.nativeEvent.shiftKey)}
 *   />
 *   // And on the <tr> if you want Shift+click anywhere on the row:
 *   <tr onClick={(e) => { if (e.shiftKey) toggle(row.id, idx, items, true); }}>
 *
 * Behaviour:
 *   - Plain click toggles a single id.
 *   - Shift+click toggles every row between the last clicked index and the
 *     current index (inclusive). The destination state of the range matches
 *     the destination state of the clicked row (so if the clicked row is
 *     being selected, the whole range is selected; if being deselected, the
 *     whole range is deselected).
 */
export function useShiftSelection() {
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const lastIndexRef = useRef(null);

  const toggle = useCallback((id, index, items, shiftKey) => {
    // Snapshot the previous anchor BEFORE we mutate it, otherwise the
    // setSelectedIds updater (which runs on a later tick) would observe the
    // already-updated value and the range branch would silently no-op.
    const anchor = lastIndexRef.current;
    lastIndexRef.current = index;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const wasSelected = prev.has(id);
      if (
        shiftKey
        && anchor != null
        && anchor !== index
        && Array.isArray(items)
      ) {
        const lo = Math.min(anchor, index);
        const hi = Math.max(anchor, index);
        const targetState = !wasSelected;
        for (let i = lo; i <= hi; i += 1) {
          const it = items[i];
          const rid = it?.id ?? it?.group_key;
          if (rid == null) continue;
          if (targetState) next.add(rid);
          else next.delete(rid);
        }
      } else if (wasSelected) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback((items) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const getId = (i) => i?.id ?? i?.group_key;
      const all = items.length > 0 && items.every((i) => prev.has(getId(i)));
      if (all) items.forEach((i) => next.delete(getId(i)));
      else items.forEach((i) => next.add(getId(i)));
      return next;
    });
    lastIndexRef.current = null;
  }, []);

  const clear = useCallback(() => {
    setSelectedIds(new Set());
    lastIndexRef.current = null;
  }, []);

  const isSelected = useCallback((id) => selectedIds.has(id), [selectedIds]);

  const allSelected = useCallback(
    (items) => items.length > 0 && items.every((i) => selectedIds.has(i?.id ?? i?.group_key)),
    [selectedIds],
  );

  return { selectedIds, setSelectedIds, isSelected, toggle, toggleAll, allSelected, clear };
}
